import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useTheme } from '../context/ThemeContext';

// Fix leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function USACricketAtlas() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [venues, setVenues] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedState, setSelectedState] = useState('All');
  const [filteredVenues, setFilteredVenues] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterVenues();
  }, [venues, selectedState]);

  const fetchData = async () => {
    try {
      const [venuesRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/venues`),
        axios.get(`${API}/leagues`)
      ]);
      setVenues(venuesRes.data);
      setLeagues(leaguesRes.data);
      setFilteredVenues(venuesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Error:', error);
    }
  };

  const filterVenues = () => {
    if (selectedState === 'All') {
      setFilteredVenues(venues);
    } else {
      setFilteredVenues(venues.filter(v => v.state === selectedState));
    }
  };

  const usStates = [...new Set(venues.map(v => v.state))];

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="page-title">USA Cricket Atlas</h1>
          <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Interactive map of cricket venues and leagues across America</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2">
            <Card className="glass-card p-0 overflow-hidden" style={{ height: '600px' }} data-testid="map-container">
              <MapContainer 
                center={[37.0902, -95.7129]} 
                zoom={4} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {filteredVenues.map((venue) => (
                  <Marker 
                    key={venue.id} 
                    position={[venue.latitude, venue.longitude]}
                  >
                    <Popup>
                      <div className="text-black" data-testid={`venue-popup-${venue.id}`}>
                        <h3 className="font-bold">{venue.name}</h3>
                        {venue.type && (
                          <div className={`inline-block px-2 py-1 rounded text-xs font-bold mb-1 ${
                            venue.type === 'MLC' ? 'bg-purple-500 text-white' :
                            venue.type === 'MiLC' ? 'bg-blue-500 text-white' :
                            'bg-gray-500 text-white'
                          }`}>
                            {venue.type}
                          </div>
                        )}
                        <p className="text-sm">{venue.city}, {venue.state}</p>
                        {venue.capacity && <p className="text-xs">Capacity: {venue.capacity}</p>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Filter */}
            <Card className="glass-card" data-testid="state-filter">
              <h3 className="font-bold mb-4">Filter by State</h3>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All States</SelectItem>
                  {usStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Card>

            {/* Leagues */}
            <Card className="glass-card" data-testid="leagues-list">
              <h3 className="font-bold mb-4 flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                Major Leagues
              </h3>
              <div className="space-y-3">
                {leagues.map((league) => (
                  <div 
                    key={league.id} 
                    className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    data-testid={`league-${league.id}`}
                  >
                    <h4 className="font-bold text-green-400">{league.name}</h4>
                    <p className="text-sm text-gray-400">{league.short_name} • {league.level}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Venues List */}
            <Card className="glass-card" data-testid="venues-list">
              <h3 className="font-bold mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-blue-400" />
                Venues ({filteredVenues.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredVenues.map((venue) => (
                  <div 
                    key={venue.id} 
                    className="p-2 rounded bg-white/5 hover:bg-white/10 transition-colors text-sm"
                    data-testid={`venue-${venue.id}`}
                  >
                    <div className="font-medium">{venue.name}</div>
                    <div className="text-xs text-gray-400">{venue.city}, {venue.state}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}